import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  XMarkIcon,
  ShieldCheckIcon,
  GlobeAltIcon,
  TagIcon,
  DocumentTextIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";
import { toast } from "../../../utils/notify";
import { Button } from "../../ui";
import { FORM_INPUT_CLASS, FORM_LABEL_CLASS, FORM_SELECT_CLASS, FORM_TEXTAREA_CLASS } from "../../ui/formStyles";
import { FRAMEWORK_TYPES, EMPTY_FW } from "./constants";

const APP_PRIMARY_COLOR = "var(--app-primary-color, #26256b)";

const FrameworkFormModal = ({ open, onClose, initial, onSave }) => {
  const [form, setForm] = useState(EMPTY_FW);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(initial ? { ...EMPTY_FW, ...initial } : EMPTY_FW);
  }, [open, initial]);

  const isEdit = Boolean(initial?.id);
  const iconInputClass = `${FORM_INPUT_CLASS} pl-9 pr-3`;
  const iconSelectClass = `${FORM_SELECT_CLASS} pl-9 pr-8`;
  const iconTextAreaClass = `${FORM_TEXTAREA_CLASS} pl-9 pr-3`;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim())               return toast.error("Framework name is required");
    if (!form.description.trim())        return toast.error("Description is required");
    if (!form.country_or_origin.trim())  return toast.error("Country / Origin is required");
    if (!form.framework_type.trim())     return toast.error("Framework type is required");
    setSaving(true);
    try {
      await onSave(form);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm"
            onClick={onClose}
          />

          {/* Modal Panel */}
          <motion.div
            key="modal"
            initial={{ opacity: 0, y: -24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.97 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
            className="relative bg-white rounded-2xl shadow-2xl w-full max-w-xl flex flex-col max-h-[92vh] overflow-hidden"
          >
            {/* Header */}
            <div
              className="flex items-center gap-4 px-6 py-5 border-b border-gray-100 rounded-t-2xl"
              style={{
                background: `linear-gradient(90deg, color-mix(in srgb, ${APP_PRIMARY_COLOR} 85%, black 15%), ${APP_PRIMARY_COLOR})`,
              }}
            >
              <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 bg-white/20 rounded-xl">
                <ShieldCheckIcon className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-base font-semibold text-white leading-tight">
                  {isEdit ? "Edit Framework" : "New Framework"}
                </h3>
                <p className="text-xs text-white/80 mt-0.5">
                  {isEdit
                    ? "Update the framework details below"
                    : "Add a new framework to the master catalog"}
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="flex-shrink-0 p-1.5 text-white/70 hover:text-white hover:bg-white/20 rounded-lg transition-colors"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <form onSubmit={handleSubmit} id="fw-form" className="flex-1 overflow-y-auto">
              <div className="px-6 py-5 space-y-5">

                {/* Framework Name */}
                <div>
                  <label className={FORM_LABEL_CLASS}>
                    Framework Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <ShieldCheckIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    <input
                      type="text"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      placeholder="e.g. ISO 27001:2022"
                      className={iconInputClass}
                    />
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className={FORM_LABEL_CLASS}>
                    Description <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <DocumentTextIcon className="absolute left-3 top-3 w-4 h-4 text-gray-400 pointer-events-none" />
                    <textarea
                      name="description"
                      rows={3}
                      value={form.description}
                      onChange={handleChange}
                      placeholder="Brief description of the framework's scope and purpose…"
                      className={iconTextAreaClass}
                    />
                  </div>
                </div>

                {/* Country & Type row */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={FORM_LABEL_CLASS}>
                      Country / Origin <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <GlobeAltIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                      <input
                        type="text"
                        name="country_or_origin"
                        value={form.country_or_origin}
                        onChange={handleChange}
                        placeholder="e.g. International"
                        className={iconInputClass}
                      />
                    </div>
                  </div>
                  <div>
                    <label className={FORM_LABEL_CLASS}>
                      Framework Type <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <TagIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                      <select
                        name="framework_type"
                        value={form.framework_type}
                        onChange={handleChange}
                        className={iconSelectClass}
                      >
                        <option value="">Select type…</option>
                        {form.framework_type && !FRAMEWORK_TYPES.includes(form.framework_type) && (
                          <option value={form.framework_type}>{form.framework_type}</option>
                        )}
                        {FRAMEWORK_TYPES.map((t) => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Active toggle */}
                <div className="flex items-center justify-between px-4 py-3 bg-gray-50 rounded-xl border border-gray-200">
                  <div className="flex items-center gap-3">
                    <div
                      className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${form.is_active ? "" : "bg-gray-200"}`}
                      style={form.is_active ? { backgroundColor: "color-mix(in srgb, var(--app-primary-color, #26256b) 14%, white)" } : undefined}
                    >
                      <CheckCircleIcon className={`w-4 h-4 ${form.is_active ? "" : "text-gray-400"}`} style={form.is_active ? { color: APP_PRIMARY_COLOR } : undefined} />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Active Status</p>
                      <p className="text-xs text-gray-500">
                        {form.is_active ? "Framework is visible and in use" : "Framework is hidden from users"}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={form.is_active}
                    aria-label="Toggle framework active status"
                    onClick={() => setForm((prev) => ({ ...prev, is_active: !prev.is_active }))}
                    className={`relative inline-flex h-8 w-16 flex-shrink-0 cursor-pointer rounded-full border transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                      form.is_active ? "shadow-sm" : "bg-white border-gray-300"
                    }`}
                    style={
                      form.is_active
                        ? {
                            backgroundColor: APP_PRIMARY_COLOR,
                            borderColor: APP_PRIMARY_COLOR,
                          }
                        : undefined
                    }
                  >
                    <span className="sr-only">{form.is_active ? "Enabled" : "Disabled"}</span>

                    <span className="absolute inset-y-0 left-2 flex items-center pointer-events-none">
                      <span
                        className={`text-[10px] font-semibold tracking-wide transition-opacity duration-150 ${
                          form.is_active ? "opacity-100" : "opacity-45"
                        }`}
                        style={form.is_active ? { color: "#FFFFFF" } : { color: "#9CA3AF" }}
                      >
                        ON
                      </span>
                    </span>

                    <span className="absolute inset-y-0 right-2 flex items-center pointer-events-none">
                      <span
                        className={`text-[10px] font-semibold tracking-wide text-gray-500 transition-opacity duration-150 ${
                          form.is_active ? "opacity-45" : "opacity-100"
                        }`}
                      >
                        OFF
                      </span>
                    </span>

                    <span
                      className={`pointer-events-none inline-block h-6 w-6 rounded-full bg-white border border-gray-200 shadow-sm transform ring-0 transition duration-200 ease-in-out z-10 mt-[3px] ${
                        form.is_active ? "translate-x-9" : "translate-x-0.5"
                      }`}
                    />
                  </button>
                </div>

              </div>
            </form>

            {/* Footer */}
            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50/80 rounded-b-2xl">
              <p className="text-xs text-gray-400">
                <span className="text-red-400">*</span> Required fields
              </p>
              <div className="flex items-center gap-3">
                <Button type="button" variant="secondary" size="sm" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  form="fw-form"
                  variant="primary"
                  size="sm"
                  disabled={saving}
                  loading={saving}
                >
                  {saving ? "Saving…" : isEdit ? "Update Framework" : "Create Framework"}
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default FrameworkFormModal;

